import { DeliveryContextFooter } from '@/components/content/ProductDetails/parts/DeliveryMethodSelector/parts/DeliveryContextFooter';
import { HomeDeliveryOption } from '@/components/content/ProductDetails/parts/DeliveryMethodSelector/parts/HomeDeliveryOption';
import { PickupOption } from '@/components/content/ProductDetails/parts/DeliveryMethodSelector/parts/PickupOption';
import { deliveryMethodSelectorSX } from '@/components/content/ProductDetails/parts/DeliveryMethodSelector/styles/deliveryMethodSelector';
import { isSelectableStatus } from '@/components/content/ProductDetails/parts/DeliveryMethodSelector/utils/isSelectableStatus';
import { PaperCard } from '@/components/content/ProductDetails/parts/PaperCards';
import DynamicIcon from '@/components/icons/DynamicIcon';
import { usePdpDeliveryOptions } from '@/data/Content/PdpDeliveryOptions';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { ContentContext } from '@/data/context/content';
import { useDeliveryLocationPanel } from '@/data/context/deliveryLocationPanelCtx';
import { useLocalization } from '@/data/Localization';
import { useStoreIDS } from '@/data/state/useStoreIDs';
import { ProductType } from '@/data/types/Product';
import { logEvent } from '@/data/utils/logEventsUtils';
import { useMobileApp } from '@/utils/useMobileApp';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { FC, MouseEvent, useContext, useEffect, useRef } from 'react';
import { MATERIAL_SYMBOL_ICONS } from 'shared/custom/constants/commonConstants';

type Props = {
	product: ProductType | undefined;
};

/**
 * PDP delivery method selector (story 159070: shell/selection; 159071: per-option
 * date/cutoff/counter content; 159073: disabled states, reason messages, and the
 * zip-restricted message block). Both options come from `usePdpDeliveryOptions`,
 * which resolves inventory once and is shared with tracking so the
 * reported delivery days always match what is rendered here.
 */
export const DeliveryMethodSelector: FC<Props> = ({ product }) => {
	const { physicalStoreId } = useStoreIDS();
	const { isDeliverySelected, setIsDeliverySelected } = useContext(ContentContext) as ReturnType<
		typeof useProductDetails
	>;
	const { openDeliveryPanel } = useDeliveryLocationPanel();
	const { DeliveryMethodSelector: labels } = useLocalization('HDMProductDetail');

	const { pickup, delivery, isInventoryLoading } = usePdpDeliveryOptions(product);
	const { isMobileApp } = useMobileApp();

	const handleChangeZipCode = (event: MouseEvent<HTMLElement>) => {
		event.stopPropagation();
		if (isMobileApp) {
			logEvent('openZipCodeSelector');
		} else {
			openDeliveryPanel();
		}
	};

	useEffect(() => {
		const currentIsSelectable = isDeliverySelected
			? isSelectableStatus(delivery.status)
			: isSelectableStatus(pickup.status);
		if (currentIsSelectable) return;

		if (isSelectableStatus(delivery.status)) {
			setIsDeliverySelected(true);
		} else if (isSelectableStatus(pickup.status)) {
			setIsDeliverySelected(false);
		}
	}, [isDeliverySelected, pickup.status, delivery.status, setIsDeliverySelected]);

	// 163146 AC7/AC12: the express variant selects "Envío a domicilio" by default,
	// overriding the 159070 rules (which still apply otherwise, AC8). Applied once
	// per product as it loads, so a customer who then picks pickup keeps that choice.
	const expressDefaultAppliedFor = useRef<string | undefined>();
	const productKey = product?.partNumber;
	useEffect(() => {
		if (delivery.status !== 'same-day' || expressDefaultAppliedFor.current === productKey) return;
		expressDefaultAppliedFor.current = productKey;
		setIsDeliverySelected(true);
	}, [delivery.status, productKey, setIsDeliverySelected]);

	const isLoading =
		pickup.status === 'pending' || delivery.status === 'pending' || isInventoryLoading;
	const showRestrictedZipMessage = delivery.status === 'blocked-zip';
	// When only one option shows an EDD line, pin both stock counters to the bottom
	// so the availability text sits at the end of the card instead of floating mid-card.
	const alignStockBottom =
		(pickup.status === 'with-date') !==
		(delivery.status === 'with-date' || delivery.status === 'same-day');

	return (
		<PaperCard elevation={1} id="deliverySelector" sx={deliveryMethodSelectorSX.container}>
			<Stack sx={deliveryMethodSelectorSX.selectors}>
				{isLoading ? (
					<>
						<Skeleton variant="text" sx={deliveryMethodSelectorSX.skeletonOption} />
						<Skeleton variant="text" sx={deliveryMethodSelectorSX.skeletonOption} />
					</>
				) : (
					<>
						<PickupOption
							pickup={pickup}
							alignStockBottom={alignStockBottom}
							physicalStoreId={physicalStoreId}
						/>
						<HomeDeliveryOption delivery={delivery} alignStockBottom={alignStockBottom} />
					</>
				)}
			</Stack>
			{!isLoading && (
				<DeliveryContextFooter
					isDeliverySelected={isDeliverySelected}
					physicalStoreId={physicalStoreId}
					pickup={pickup}
					delivery={delivery}
				/>
			)}
			{!isLoading && showRestrictedZipMessage ? (
				<Stack
					className="delivery-selector__restricted-message"
					sx={deliveryMethodSelectorSX.restrictedMessage}
				>
					<Box display="flex" alignItems="center" justifyContent="left">
						<DynamicIcon
							name={MATERIAL_SYMBOL_ICONS.block}
							size={24}
							sx={deliveryMethodSelectorSX.blockIcon}
						/>
						<Typography variant="bodySubText" color="grey.300">
							{labels.RestrictedZipMessagePrefix.t()}
							<Typography
								component="span"
								variant="bodySubText"
								id="deliveryUpdateZipCode"
								sx={deliveryMethodSelectorSX.restrictedMessageLink}
								onClick={handleChangeZipCode}
							>
								{labels.RestrictedZipMessageLink.t()}
							</Typography>
						</Typography>
					</Box>
				</Stack>
			) : null}
		</PaperCard>
	);
};
